import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {  validatePostalCode } from '../../validationUtils';
import { getAddress } from '../../utilities';
import '../student/Newstudent.css';
import { useAuth } from '../../context/AuthContext.js';
import LocationSelectorForForm from '../locaties/LocationSelectorForForm.js';
import AllNotes from '../notes/AllNotes.js';
import IBANInput from '../../components/IBANInput.js';

function EditStudent() {
    const { id } = useParams();
    const [allIds, setAllIds] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [postalcodeValidation, setPostalCodeValidation] = useState({ valid: true, message: '' });
    const [studentExists, setStudentExists] = useState(false);
    const [locations, setLocations] = useState([]);
    const { userInfo } = useAuth();
    const userRole = userInfo?.role || '';
    const [assignedParents, setAssignedParents] = useState([]);
    const [allParents, setAllParents] = useState([]);
    const [selectedParentId, setSelectedParentId] = useState('');
    const [parentSearch, setParentSearch] = useState('');
    const [selectedRelationship, setSelectedRelationship] = useState('vader');

    const [values, setValues] = useState({
        firstname: '',
        lastname: '',
        birthdate: '',
        datein: '',
        dateout: '',
        street: '',
        housenr: '',
        postalcode: '',
        city: '',
        school: '',
        allergy: '',
        iban: '',
        accountowner: '',
        monthlyamount: '',
        comments: '',
        mailing: '',
        toestemmingfoto: '',
        location_id: '',
        customer_student_number: ''
    });

    const navigate = useNavigate();
    const token = sessionStorage.getItem('jwtToken');

    const relationshipLabelMap = {
        vader: 'Vader',
        moeder: 'Moeder',
        voogd: 'Voogd',
        overig: 'Anders'
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const updateStudent = async () => {
            try {
                const cleanValues = {
                    ...values,
                    dateout: values.dateout === '0000-00-00' || values.dateout === '' ? null : values.dateout
                };

                const res = await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/students/updatestudent/${id}`,
                    cleanValues,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );

                if (res.data.Status === 'Success') {
                    navigate('/allstudents');
                } else {
                    alert('Error: ' + res.data.status);
                }
            } catch (error) {
                console.error('Error in updating student:', error);
            }
        };

        updateStudent();
    };

    const handleCancel = () => {
        navigate('/allstudents');
    };

    const fetchStudent = useCallback(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/students/getstudent/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then((response) => {
                if (response.status === 204) {
                    console.warn('Geen studentgegevens gevonden (204 No Content)');
                    return;
                }

                if (response.status === 200) {
                    setStudentExists(true);
                    setValues((prev) => ({
                        ...prev,
                        ...response.data.Data
                    }));
                } else {
                    console.error('Unexpected response status:', response.status);
                    setStudentExists(false);
                }
            })
            .catch((error) => {
                console.error('Error fetching student data', error);
            });
    }, [id, token]);

    useEffect(() => {
        fetchStudent();
    }, [fetchStudent]);

    useEffect(() => {
        if (userRole === 'super_admin') {
            axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/location/allLocations`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )
                .then((res) => {
                    const locs = res.data.data || res.data;
                    setLocations(Array.isArray(locs) ? locs : []);
                })
                .catch((err) => console.error('Error fetching locations', err));
        }
    }, [userRole, token]);

    useEffect(() => {
        const fetchStudentParents = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/student-parents/student/${id}/parents`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAssignedParents(res.data.data || []);
            } catch (err) {
                console.error('Error fetching student parents', err);
            }
        };

        fetchStudentParents();
    }, [id, token]);

    useEffect(() => {
    const fetchAllParents = async () => {
        try {
            const params = {
                limit: 50,
                page: 1,
            };

            if (parentSearch.trim()) {
                params.search = parentSearch.trim();
            }

            if (!userInfo?.is_single_user && values.location_id) {
                params.frontend_location_id = values.location_id;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/parent/allparents`,
                {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const data = res.data.data || res.data;
            setAllParents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching all parents', err);
        }
    };

    const timer = setTimeout(() => {
        if (userInfo?.is_single_user || values.location_id) {
            fetchAllParents();
        } else {
            setAllParents([]);
        }
    }, 300);

    return () => clearTimeout(timer);
}, [token, values.location_id, userInfo?.is_single_user, parentSearch]);

    useEffect(() => {
        const fetchAllIds = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/students/allids`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAllIds(response.data.ids || []);
            } catch (err) {
                console.error('Fout bij ophalen alle student-IDs:', err);
            }
        };

        fetchAllIds();
    }, [token]);

    useEffect(() => {
        if (allIds.length === 0) return;
        setCurrentIndex(allIds.indexOf(id));
    }, [allIds, id]);

    const linkedParentIds = useMemo(
        () => assignedParents.map((p) => p.parent_id),
        [assignedParents]
    );

    const availableParents = useMemo(() => {
    return allParents.filter((p) => !linkedParentIds.includes(p.parent_id));
}, [allParents, linkedParentIds]);

    const handleAddParent = async () => {
        if (!selectedParentId) return;

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/student-parents/add`,
                {
                    studentId: id,
                    parentId: selectedParentId,
                    relationship: selectedRelationship
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const addedParent = allParents.find((p) => p.parent_id === selectedParentId);
            if (addedParent) {
                setAssignedParents((prev) => [
                    ...prev,
                    { ...addedParent, relationship: selectedRelationship }
                ]);
            }

            setSelectedParentId('');
            setSelectedRelationship('vader');
            setParentSearch('');
        } catch (err) {
            console.error('Error linking parent to student', err);
            alert('Kon ouder niet koppelen.');
        }
    };

    const handleRemoveParent = async (parent) => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/student-parents/remove`,
                {
                    data: { studentId: id, parentId: parent.parent_id },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            setAssignedParents((prev) => prev.filter((p) => p.parent_id !== parent.parent_id));
        } catch (err) {
            console.error('Error removing parent from student', err);
            alert('Kon ouder niet ontkoppelen.');
        }
    };

    return (
        <div>
            {studentExists ? (
                <div className='bg-white p-4'>
                    <div><h2 className='text-center'>Bewerk leerling</h2></div>
                    <hr className='seperator' />

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                if (currentIndex > 0) {
                                    const prevId = allIds[currentIndex - 1];
                                    navigate(`/editstudent/${prevId}`);
                                }
                            }}
                            disabled={currentIndex <= 0}
                        >
                            <i className="bi bi-arrow-left"></i> Vorige
                        </button>
                        <span>Leerling {currentIndex + 1} van {allIds.length}</span>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                if (currentIndex < allIds.length - 1) {
                                    const nextId = allIds[currentIndex + 1];
                                    navigate(`/editstudent/${nextId}`);
                                }
                            }}
                            disabled={currentIndex < 0 || currentIndex >= allIds.length - 1}
                        >
                            Volgende <i className="bi bi-arrow-right"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className='form-student'>
                        <div className='row'>
                            <LocationSelectorForForm
                                userRole={userRole}
                                value={values.location_id ?? ''}
                                onChange={(e) =>
                                    setValues({
                                        ...values,
                                        location_id: e.target.value.trim()
                                    })
                                }
                                locations={locations}
                            />
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <p>Studentnummer: {values.customer_student_number || ''}</p>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label htmlFor='firstname'>Voornaam<span style={{ color: 'red' }}>*</span></label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="firstname"
                                    placeholder="Voornaam"
                                    required
                                    value={values.firstname}
                                    onChange={(e) => setValues({ ...values, firstname: e.target.value })}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='lastname'>Achternaam<span style={{ color: 'red' }}>*</span></label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="lastname"
                                    placeholder="Achternaam"
                                    required
                                    value={values.lastname}
                                    onChange={(e) => setValues({ ...values, lastname: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label htmlFor='birthdate'>Geboortedatum<span style={{ color: 'red' }}>*</span></label>
                                <input
                                    className='form-control rounded-0'
                                    type="date"
                                    name="birthdate"
                                    placeholder="Geboortedatum"
                                    required
                                    value={values.birthdate}
                                    onChange={(e) => setValues({ ...values, birthdate: e.target.value })}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='datein'>Datum Inschrijving<span style={{ color: 'red' }}>*</span></label>
                                <input
                                    className='form-control rounded-0'
                                    type="date"
                                    name="datein"
                                    placeholder="Datum Inschrijving"
                                    required
                                    value={values.datein}
                                    onChange={(e) => setValues({ ...values, datein: e.target.value })}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='dateout'>Datum Uitschrijving</label>
                                <input
                                    className='form-control rounded-0'
                                    type="date"
                                    name="dateout"
                                    placeholder="Datum Uitschrijving"
                                    value={values.dateout}
                                    onChange={(e) => setValues({ ...values, dateout: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label htmlFor='postalcode'>Postcode</label>
                                <input
                                    className={`form-control rounded-0 ${postalcodeValidation.valid ? '' : 'is-invalid'}`}
                                    type="text"
                                    name="postalcode"
                                    placeholder="Postcode"
                                    value={values.postalcode}
                                    onChange={(e) => {
                                        setValues({ ...values, postalcode: e.target.value });
                                        validatePostalCode(e.target.value);
                                    }}
                                    onBlur={(e) => {
                                        const isValid = validatePostalCode(e.target.value);
                                        if (!isValid) {
                                            setPostalCodeValidation({ valid: false, message: 'Ongeldige Postcode' });
                                        } else {
                                            setPostalCodeValidation({ valid: true, message: '' });
                                        }
                                    }}
                                />
                                {!postalcodeValidation.valid && (
                                    <div className="invalid-feedback">{postalcodeValidation.message}</div>
                                )}
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='housenr'>Huisnummer</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="housenr"
                                    placeholder="Huisnummer"
                                    value={values.housenr}
                                    onChange={(e) => setValues({ ...values, housenr: e.target.value })}
                                    onBlur={() => getAddress(values, setValues, postalcodeValidation)}
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label htmlFor='street'>Straat</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="street"
                                    placeholder="Straat"
                                    readOnly
                                    value={values.street}
                                    onChange={(e) => setValues({ ...values, street: e.target.value ?? ' ' })}
                                    style={{ backgroundColor: '#FAFAFA' }}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='city'>Plaats</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="city"
                                    placeholder="Plaats"
                                    readOnly
                                    value={values.city}
                                    onChange={(e) => setValues({ ...values, city: e.target.value ?? ' ' })}
                                    style={{ backgroundColor: '#FAFAFA' }}
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label htmlFor='school'>School</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="school"
                                    placeholder="School"
                                    value={values.school}
                                    onChange={(e) => setValues({ ...values, school: e.target.value })}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='allergy'>Allergie</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="allergy"
                                    placeholder="Allergie"
                                    value={values.allergy}
                                    onChange={(e) => setValues({ ...values, allergy: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className='mb-3'>
                            <label htmlFor='comments'>Opmerkingen</label>
                            <textarea
                                className='form-control rounded-0'
                                rows="5"
                                cols="50"
                                name="comments"
                                placeholder="Opmerkingen"
                                value={values.comments}
                                onChange={(e) => setValues({ ...values, comments: e.target.value })}
                            />
                        </div>

                        <hr className='seperator' />

                        <div className='row'>
                            <IBANInput
                                value={values.iban}
                                onChange={(iban) => setValues((v) => ({ ...v, iban }))}
                            />
                            <div className='mb-3 col'>
                                <label htmlFor='accountowner'>Rekeninghouder</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="accountowner"
                                    placeholder="Rekeninghouder"
                                    value={values.accountowner}
                                    onChange={(e) => setValues({ ...values, accountowner: e.target.value })}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='monthlyamount'>Maandbedrag</label>
                                <input
                                    className='form-control rounded-0'
                                    type="text"
                                    name="monthlyamount"
                                    placeholder="Maandbedrag"
                                    value={values.monthlyamount}
                                    onChange={(e) => setValues({ ...values, monthlyamount: e.target.value })}
                                />
                            </div>
                        </div>

                        <hr className='seperator' />
                        <p className='text-center font-italic'>AVG</p>
                        <hr className='seperator' />

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label htmlFor='mailing'>Toestemming Nieuwsbrief</label>
                                <div>
                                    <select
                                        name="mailing"
                                        value={values.mailing}
                                        onChange={(e) => setValues({ ...values, mailing: e.target.value })}
                                    >
                                        <option value="">Maak een keuze</option>
                                        <option value="ja">Ja</option>
                                        <option value="nee">Nee</option>
                                    </select>
                                </div>
                            </div>
                            <div className='mb-3 col'>
                                <label htmlFor='toestemmingfoto'>Toestemming Foto&apos;s</label>
                                <div>
                                    <select
                                        name="toestemmingfoto"
                                        value={values.toestemmingfoto}
                                        onChange={(e) => setValues({ ...values, toestemmingfoto: e.target.value })}
                                    >
                                        <option value="">Maak een keuze</option>
                                        <option value="ja">Ja</option>
                                        <option value="nee">Nee</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label>Gekoppelde Ouders</label>
                            {!userInfo?.is_single_user && !values.location_id && (
                                <div className="alert alert-info">
                                    Koppel eerst een locatie aan deze leerling om ouders te kunnen selecteren.
                                    De ouders die je wilt koppelen, moeten ook aan dezelfde locatie gekoppeld zijn.
                                </div>
                            )}
                            <ul className="list-group mb-2">
                                {assignedParents.map((p) => (
                                    <li
                                        key={p.parent_id}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                    >
                                        <span>
                                            <Link to={`/ouder/edit/${p.parent_id}`}>
                                                {p.firstname} {p.lastname}
                                            </Link>
                                            {' '}– {p.mobile} – {p.email} ({relationshipLabelMap[p.relationship] || p.relationship || '-'})
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveParent(p)}
                                            className="btn btn-sm btn-danger"
                                        >
                                            Verwijder
                                        </button>
                                    </li>
                                ))}
                                {assignedParents.length === 0 && (
                                    <li className="list-group-item">Nog geen ouders gekoppeld.</li>
                                )}
                            </ul>

                            <div className="row mb-2">
                                <div className="col-md-5">
                                    <input
                                        type="text"
                                        className="form-control rounded-0"
                                        placeholder="Zoek ouder (voor- of achternaam)"
                                        value={parentSearch}
                                        onChange={(e) => setParentSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <select
                                        className="form-select rounded-0"
                                        value={selectedParentId}
                                        onChange={(e) => setSelectedParentId(e.target.value)}
                                    >
                                        <option value="">-- Selecteer ouder --</option>
                                        {availableParents.map((p) => (
                                            <option key={p.parent_id} value={p.parent_id}>
                                                {p.firstname} {p.lastname}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <select
                                        className="form-select rounded-0"
                                        value={selectedRelationship}
                                        onChange={(e) => setSelectedRelationship(e.target.value)}
                                    >
                                        <option value="vader">Vader</option>
                                        <option value="moeder">Moeder</option>
                                        <option value="voogd">Voogd</option>
                                        <option value="overig">Anders</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <button
                                        type="button"
                                        onClick={handleAddParent}
                                        className="btn btn-outline-primary w-100"
                                        disabled={!selectedParentId}
                                    >
                                        Ouder koppelen
                                    </button>
                                </div>
                            </div>
                        </div>

                        <hr className='seperator' />

                        <div className='row'>
                            <AllNotes studentId={id} />
                        </div>

                        <div className='row'>
                            <div className='col-md-6'>
                                <button
                                    type="button"
                                    className='btn btn-secondary w-100 rounded-0'
                                    onClick={handleCancel}
                                >
                                    Annuleren
                                </button>
                            </div>
                            <div className='col-md-6'>
                                <button type='submit' className='btn btn-secondary w-100 rounded-0'>
                                    Opslaan
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            ) : (
                <div className='bg-white p-4'>
                    <h2 className='text-center'>Onbekende leerling</h2>
                    <button
                        type="button"
                        className='btn btn-secondary w-100 rounded-0'
                        onClick={handleCancel}
                    >
                        Terug
                    </button>
                </div>
            )}
            <div className='spacer-bottom'></div>
        </div>
    );
}

export default EditStudent;